import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, X, Smartphone, Apple, Chrome, Monitor } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { usePwaInstall } from '../../lib/usePwaInstall';
import { toast } from 'sonner';

function ManualGuide({ platform, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-white/10 max-w-sm" data-testid="install-manual-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Download className="w-5 h-5 text-violet-400" />
            Add Jixxeo AI to your {platform.ios ? 'Home Screen' : 'phone'}
          </DialogTitle>
        </DialogHeader>
        {platform.ios ? (
          <ol data-testid="guide-ios-list" className="space-y-3 text-sm">
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span><span>Make sure you're in <strong>Safari</strong>.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span><span>Tap the <strong>Share</strong> icon at the bottom of Safari.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span><span>Scroll and tap <strong>Add to Home Screen</strong>.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">4</span><span>Tap <strong>Add</strong> — Jixxeo AI is now on your Home Screen.</span></li>
          </ol>
        ) : platform.android ? (
          <ol data-testid="guide-android-list" className="space-y-3 text-sm">
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span><span>Open your browser's <strong>menu</strong> (the 3 dots).</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span><span>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span><span>Confirm — Jixxeo AI opens like a real app.</span></li>
          </ol>
        ) : (
          <ol data-testid="guide-desktop-list" className="space-y-3 text-sm">
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span><span>Use <strong>Chrome</strong>, <strong>Edge</strong> or <strong>Brave</strong>.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span><span>Click the <strong>Install</strong> icon in the right side of the address bar.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full gradient-brand text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span><span>Choose <strong>Install</strong> — Jixxeo AI opens in its own window.</span></li>
          </ol>
        )}
        <Link to="/install" className="text-xs text-violet-400 hover:underline text-center mt-3">See all install options →</Link>
      </DialogContent>
    </Dialog>
  );
}

/** The big homepage card version */
export function InstallHomeBanner() {
  const pwa = usePwaInstall();
  const [showGuide, setShowGuide] = useState(false);

  if (pwa.installed || pwa.isDismissed) return null;
  // Hide only when there's genuinely no viable install path
  const canAuto = pwa.installable;
  const canManual = pwa.canPromptIOS || pwa.platform.android || (!pwa.platform.ios && !pwa.platform.android);
  if (!canAuto && !canManual) return null;

  const onInstall = async () => {
    if (canAuto) {
      const r = await pwa.promptInstall();
      if (r.outcome === 'accepted') toast.success('Installing Jixxeo AI…');
      else if (r.outcome === 'unavailable') setShowGuide(true);
    } else {
      setShowGuide(true);
    }
  };

  const Icon = pwa.platform.ios ? Apple : pwa.platform.android ? Smartphone : Monitor;

  return (
    <>
      <div
        data-testid="home-install-banner"
        className="relative overflow-hidden rounded-2xl p-4 sm:p-5 border border-violet-500/40 bg-gradient-to-br from-violet-600/25 via-fuchsia-600/15 to-cyan-600/20"
      >
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-violet-500/30 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl gradient-brand flex items-center justify-center shadow-lg shadow-violet-500/40 flex-shrink-0">
            <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-base sm:text-lg leading-tight">Get Jixxeo AI on your phone</div>
            <div className="text-xs sm:text-sm text-slate-300 mt-0.5">Install for faster access — earn a <span className="text-amber-300 font-bold">+250 JXP</span> bonus the first time you install.</div>
          </div>
        </div>
        <div className="relative flex flex-wrap gap-2 mt-4">
          <Button
            data-testid="home-install-primary"
            onClick={onInstall}
            className="gradient-brand text-white shadow-lg shadow-violet-500/30 flex-1 sm:flex-none"
          >
            <Download className="w-4 h-4 mr-1.5" /> Install Jixxeo AI
          </Button>
          <Button
            data-testid="home-install-later"
            variant="outline"
            onClick={() => pwa.dismiss(1)}
            className="border-white/20 hover:bg-white/5"
          >
            Maybe Later
          </Button>
        </div>
      </div>
      {showGuide && <ManualGuide platform={pwa.platform} onClose={() => setShowGuide(false)} />}
    </>
  );
}

/** The tiny bottom toast version (kept, but only on non-home routes) */
export default function InstallPrompt() {
  const pwa = usePwaInstall();
  const [showGuide, setShowGuide] = useState(false);

  if (pwa.installed || pwa.isDismissed) return null;
  const canAuto = pwa.installable;
  const canManual = pwa.canPromptIOS;
  if (!canAuto && !canManual) return null;

  const onInstall = async () => {
    if (canAuto) {
      const r = await pwa.promptInstall();
      if (r.outcome === 'unavailable') setShowGuide(true);
    } else {
      setShowGuide(true);
    }
  };

  return (
    <>
      <div
        data-testid="install-prompt"
        className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl shadow-violet-500/30 max-w-md w-[calc(100%-2rem)] border border-violet-500/25"
      >
        <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Install Jixxeo AI</div>
          <div className="text-xs text-slate-400 truncate">{canAuto ? 'Faster, offline-ready.' : 'Tap Share → Add to Home Screen'}</div>
        </div>
        <Button
          data-testid="install-btn"
          size="sm"
          className="gradient-brand text-white"
          onClick={onInstall}
        >Install</Button>
        <button
          data-testid="install-dismiss"
          onClick={() => pwa.dismiss(1)}
          className="text-slate-400 hover:text-white p-1"
          aria-label="Later"
        ><X className="w-4 h-4" /></button>
      </div>
      {showGuide && <ManualGuide platform={pwa.platform} onClose={() => setShowGuide(false)} />}
    </>
  );
}
